import React, { useCallback, useRef, useState } from 'react'

import { type InfiniteData } from '@tanstack/react-query'
import { type CellContext } from '@tanstack/react-table'
import { formatDistanceToNow } from 'date-fns'
import Link from 'next/link'
import {
  HiEllipsisHorizontal,
  HiOutlineEye,
  HiOutlineTag,
  HiOutlineTrash,
} from 'react-icons/hi2'
import {
  LuClock,
  LuFileText,
  LuMail,
  LuPhone,
  LuUserPlus,
} from 'react-icons/lu'
import { toast } from 'sonner'
import { twMerge } from 'tailwind-merge'

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/common/AlertDialog'
import Avatar, {
  NoAssigneeAvatar,
  UserAvatar,
} from '@/components/common/Avatar'
import { Badge } from '@/components/common/BadgeV2'
import Button from '@/components/common/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/common/DropdownMenu'
import IconButton from '@/components/common/IconButtonV2'
import { ResourcePermissionGate } from '@/components/common/PermissionGate'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/common/Select'
import { AvatarSkeleton, Skeleton } from '@/components/common/Skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'
import Label from '@/components/form/Label'
import { getCompanyName } from '@/components/library-v2/companies/lib'
import { AddResumeDialog } from '@/components/library-v2/people/AddResumeDialog'
import { DeletePeopleDialog } from '@/components/library-v2/people/DeletePeopleDialog'
import {
  useIsYou,
  usePersonAgency,
  usePersonCompany,
  useThemeAdjustedPersonAvatarColor,
} from '@/components/library-v2/people/hooks'
import {
  getPersonFirstName,
  getPersonFullName,
} from '@/components/library-v2/people/lib'
import {
  usePeopleAddRelationshipStatusFilter,
  usePeopleLastSelectedRowId,
  usePeopleRelationshipStatusFilterIsActive,
  usePeopleSetConfirmDialogOpen,
  usePeopleSetLastSelectedRowId,
  usePeopleSetSelectedPersonIds,
} from '@/components/library-v2/people/Table/context'
import { PendingBadge, YouBadge } from '@/components/library-v2/ui/Badge'
import {
  RelationshipStatusBadge,
  TeamMemberBadge,
} from '@/components/library-v2/ui/RelationshipStatusBadge'
import { ShiftSelectCell } from '@/components/library-v2/ui/Table'
import { getAvailableRoles } from '@/components/settings/Organizations/InviteToOrganizationModal'
import { InlineTagSummary } from '@/components/tags/InlineTagSummary'
import { TagDropdownMenuEditSubContent } from '@/components/tags/TagDropdownMenuSubContent'

import { ensureDateStringIsUTC } from '@/helpers/date'

import { useBillingOrganization } from '@/hooks/billing/useBillingOrganization'
import { useMaximumUsersReached } from '@/hooks/billing/useBillingOrganization'
import { useCanInviteMembers } from '@/hooks/billing/useBillingOrganization'
import {
  OrganizationInvite,
  useBillingOrgInvites,
} from '@/hooks/billing/useBillingOrgInvites'
import {
  useBillingRole,
  useIsAtLeastAdmin,
} from '@/hooks/billing/useBillingRole'
import { useInviteToOrganization } from '@/hooks/billing/useInviteToOrganization'
import { useGravatar } from '@/hooks/common/useGravatar'
import { useEntityTagCombobox } from '@/hooks/library/useUpdateEntityTags'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { useOrganization } from '@/hooks/organization/useOrganization'
import { useOrganizationUser } from '@/hooks/organization/useOrganizationUsers'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { EntityPerson } from '@/types/library/Entity'

import { usePermissionLoader } from '@/services/permission-loader/context'

interface PersonAvatarProps {
  person: EntityPerson
  width?: number
  height?: number
  noTooltip?: boolean
}

export const PersonAvatar: React.FC<PersonAvatarProps> = ({
  person,
  width = 28,
  height = 28,
  noTooltip = true,
}) => {
  const { activeOrgId } = useActiveOrgId()

  const { data: organizationUser, isLoading: isOrganizationUserLoading } =
    useOrganizationUser({
      orgId: activeOrgId,
      userId: person.user_id,
    })

  const color = useThemeAdjustedPersonAvatarColor({ person })

  const noSuffixName = getPersonFullName(person, false)

  const gravatarSrc = useGravatar({
    email: organizationUser?.email || person.email || '',
    width,
    height,
  })

  const avatarUrl = organizationUser?.picture_url
    ? organizationUser.picture_url
    : gravatarSrc

  if (isOrganizationUserLoading) {
    return <AvatarSkeleton width={width} height={height} noName={true} />
  }
  if (organizationUser && !!organizationUser.picture_url) {
    return (
      <UserAvatar
        userId={organizationUser.id}
        width={width}
        height={height}
        noTooltip={noTooltip}
      />
    )
  }

  if (noSuffixName.length === 0) {
    return <NoAssigneeAvatar width={width} height={height} />
  }

  return (
    <Avatar
      src={avatarUrl}
      name={noSuffixName}
      width={width}
      height={height}
      includeLabel={false}
      noTooltip={noTooltip}
      style={{ backgroundColor: color }}
      startWithFallback={true}
    />
  )
}

export const PersonNameCell: React.FC<CellContext<EntityPerson, unknown>> = ({
  row,
}) => {
  const { activeOrgId } = useActiveOrgId()

  const { data: organizationUser } = useOrganizationUser({
    orgId: activeOrgId,
    userId: row.original.user_id,
  })

  const [addResumeDialogOpen, setAddResumeDialogOpen] = useState<boolean>(false)

  const person = row.original

  const name = organizationUser
    ? `${organizationUser.first_name} ${organizationUser.last_name}`
    : getPersonFullName(person)

  const role = person.role
  const tagIds = person.tag_ids ?? []

  return (
    <div className="flex items-center justify-between gap-3 py-3 pr-3 overflow-hidden">
      {
        // only render the dialog if it's open for performance reasons
        addResumeDialogOpen && (
          <AddResumeDialog
            person={person}
            open={addResumeDialogOpen}
            onOpenChange={setAddResumeDialogOpen}
          />
        )
      }
      <div className="flex items-center gap-3 overflow-hidden">
        <div className="flex-shrink-0">
          <PersonAvatar person={person} />
        </div>
        <div className="flex min-w-0 flex-col gap-0.5 overflow-hidden">
          <div className="flex items-center gap-2">
            <Link
              className={twMerge(
                'font-medium text-primary-900 dark:text-primary-100 hover:text-blue-600 dark:hover:text-blue-400 text-sm text-left cursor-pointer truncate',
                name.length > 0 ? '' : 'text-primary-500 dark:text-primary-400',
              )}
              href={`/orgs/${activeOrgId}/library/people/${person.id}`}
            >
              {name.length > 0 ? name : 'Name not available'}
            </Link>
          </div>
          {role && (
            <span className="text-sm text-primary-500 dark:text-primary-400 truncate">
              {role}
            </span>
          )}
          {tagIds.length > 0 ? <InlineTagSummary tagIds={tagIds} /> : null}
        </div>
      </div>
    </div>
  )
}

export const PersonBadgesCell: React.FC<CellContext<EntityPerson, unknown>> = ({
  row,
}) => {
  const person = row.original
  const [addResumeDialogOpen, setAddResumeDialogOpen] = useState<boolean>(false)

  const setConfirmDialogOpen = usePeopleSetConfirmDialogOpen()
  const setSelectedIds = usePeopleSetSelectedPersonIds()
  const addRelationshipStatusFilter = usePeopleAddRelationshipStatusFilter()
  const isStatusFilterActive = usePeopleRelationshipStatusFilterIsActive(
    person.relationship_status,
  )

  const isYou = useIsYou({ userId: person.user_id })

  // Internal people (team members) don't show relationship status
  const isInternal = person.is_internal

  const handleOpenAddResumeDialog = useCallback(() => {
    setAddResumeDialogOpen(true)
  }, [setAddResumeDialogOpen])

  const handleOpenConfirmDialogForPerson = useCallback(() => {
    setConfirmDialogOpen(true)
    setSelectedIds([person.id])
  }, [person.id, setConfirmDialogOpen, setSelectedIds])

  const handleFilterByRelationshipStatus = useCallback(() => {
    if (person.relationship_status && !isStatusFilterActive) {
      addRelationshipStatusFilter(person.relationship_status)
    }
  }, [
    person.relationship_status,
    isStatusFilterActive,
    addRelationshipStatusFilter,
  ])

  return (
    <div className="flex items-center justify-end gap-2 py-1.5 pr-3">
      {isYou && <YouBadge />}
      {addResumeDialogOpen && (
        <AddResumeDialog
          person={person}
          open={addResumeDialogOpen}
          onOpenChange={setAddResumeDialogOpen}
        />
      )}
      {/* Only show resume badge for internal people */}
      {isInternal && person.confirmed && !person.resume_file_id && (
        <ResourcePermissionGate
          resourceType="entity"
          resourceId={person.id}
          permission="write"
          fallback="hide"
        >
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild={true}>
              <button type="button" onClick={handleOpenAddResumeDialog}>
                <Badge
                  variant="destructive"
                  className="flex items-center justify-center whitespace-nowrap text-[13px] h-[22px] w-[22px] dark:bg-red-600/70 dark:hover:bg-red-600/90 dark:border-red-600 dark:text-red-200"
                >
                  <LuFileText className="!w-[13px] !h-[13px] flex-shrink-0" />
                </Badge>
              </button>
            </TooltipTrigger>
            <TooltipContent>Click to add a resume</TooltipContent>
          </Tooltip>
        </ResourcePermissionGate>
      )}
      {/* Show Team Member badge for internal people, status badge for external */}
      {isInternal ? (
        <TeamMemberBadge />
      ) : (
        <RelationshipStatusBadge
          status={person.relationship_status}
          disabled={true}
          size="sm"
          onClick={handleFilterByRelationshipStatus}
        />
      )}
      {!person.confirmed && (
        <ResourcePermissionGate
          resourceType="entity"
          resourceId={person.id}
          permission="write"
          fallback="hide"
        >
          <PendingBadge
            inTable={true}
            onClick={handleOpenConfirmDialogForPerson}
          />
        </ResourcePermissionGate>
      )}
    </div>
  )
}

export const AffiliationCell: React.FC<CellContext<EntityPerson, unknown>> = ({
  row,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const person = row.original
  const shouldShowCompany = !!person.entity_company_id

  const { data: company, isLoading: isCompanyLoading } = usePersonCompany({
    person,
  })
  const { data: agency, isLoading: isAgencyLoading } = usePersonAgency({
    person,
  })

  const isLoading = shouldShowCompany ? isCompanyLoading : isAgencyLoading

  if (isLoading) {
    return (
      <div className="py-3 pr-3">
        <Skeleton className="h-4 w-24" />
      </div>
    )
  }

  const affiliation = shouldShowCompany
    ? company
      ? {
          href: `/orgs/${activeOrgId}/library/companies/${company.id}`,
          label: getCompanyName(company),
        }
      : null
    : agency
      ? {
          href: `/orgs/${activeOrgId}/library/agencies/${agency.id}`,
          label: agency.name,
        }
      : null

  if (!affiliation) {
    return (
      <div className="py-3 pr-3">
        <span className="text-sm text-primary-500 dark:text-primary-400">
          Not specified
        </span>
      </div>
    )
  }

  return (
    <div className="py-3 pr-3 truncate">
      <Link
        href={affiliation.href}
        className="font-medium text-primary-900 dark:text-primary-100 text-sm truncate block hover:text-blue-600 dark:hover:text-blue-400"
      >
        {affiliation.label}
      </Link>
    </div>
  )
}

export const ContactCell: React.FC<CellContext<EntityPerson, unknown>> = ({
  row,
}) => {
  const person = row.original

  const { activeOrgId } = useActiveOrgId()
  const { data: organizationUser } = useOrganizationUser({
    orgId: activeOrgId,
    userId: person.user_id,
  })

  const email = organizationUser ? organizationUser.email : person.email

  return (
    <div className="py-3 pr-3">
      <div className="space-y-1">
        {email && (
          <a
            href={`mailto:${email}`}
            className="flex items-center gap-2 text-[13px] text-primary-700 dark:text-primary-300 truncate hover:text-blue-600 dark:hover:text-blue-400"
          >
            <LuMail size={14} className="flex-shrink-0" />
            <span className="truncate">{email}</span>
          </a>
        )}
        {person.phone && (
          <a
            href={`tel:${person.phone}`}
            className="flex items-center gap-2 text-[13px] text-primary-700 dark:text-primary-300 hover:text-blue-600 dark:hover:text-blue-400"
          >
            <LuPhone size={14} className="flex-shrink-0" />
            <span className="truncate">{person.phone}</span>
          </a>
        )}
        {!email && !person.phone && (
          <div className="text-sm text-primary-500 dark:text-primary-400">
            No contact info
          </div>
        )}
      </div>
    </div>
  )
}

export const LastContactedCell: React.FC<
  CellContext<EntityPerson, unknown>
> = ({ row }) => {
  const dt = row.original.last_contacted_at
  return (
    <span className="py-3 pr-3 text-sm text-primary-500 dark:text-primary-400">
      {dt
        ? formatDistanceToNow(new Date(ensureDateStringIsUTC(dt)), {
            addSuffix: true,
          })
        : 'Never'}
    </span>
  )
}

interface InviteToOrganizationDialogProps {
  person: EntityPerson
}

const InviteToOrganizationDialog: React.FC<InviteToOrganizationDialogProps> = ({
  person,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const { data: org } = useOrganization(activeOrgId)
  const { data: billingOrganization } = useBillingOrganization()
  const { mutateAsync: inviteToOrganization, isPending: isInviting } =
    useInviteToOrganization()

  const billingRole = useBillingRole()
  const availableRoles = getAvailableRoles(billingRole)

  const [open, setOpen] = useState<boolean>(false)
  const [selectedRole, setSelectedRole] = useState<string>('Member')

  const maximumUsersReached = useMaximumUsersReached()
  const canOrgInviteMembers = useCanInviteMembers()
  const isAtLeastAdmin = useIsAtLeastAdmin()

  const canInviteMembers = canOrgInviteMembers && isAtLeastAdmin

  const selectHasBeenInvited = useCallback(
    (invites: InfiniteData<PaginatedResponse<OrganizationInvite>>) => {
      return invites.pages
        .flatMap((page) => page.data)
        .some((invite) => invite.invitee_email === person.email)
    },
    [person.email],
  )

  const { data: hasBeenInvited = false } = useBillingOrgInvites({
    billingOrgId: billingOrganization?.id as UUID,
    select: selectHasBeenInvited,
  })

  const handleOpen = useCallback(() => {
    if (!canInviteMembers || maximumUsersReached) {
      return
    }
    setOpen(true)
  }, [canInviteMembers, maximumUsersReached, setOpen])

  const handleClose = useCallback(() => {
    setOpen(false)
  }, [setOpen])

  const handleInvite = useCallback(async () => {
    if (!billingOrganization?.id || !person.email) {
      return
    }
    await inviteToOrganization(
      {
        billingOrgId: billingOrganization.id as UUID,
        inviteData: {
          invitee_email: person.email,
          target_organizations: [{ organization_id: activeOrgId as UUID }],
          billing_role: selectedRole,
        },
      },
      {
        onSuccess: () => {
          setOpen(false)
          toast.success(`Invitation sent to ${name}`)
        },
      },
    )
  }, [
    activeOrgId,
    billingOrganization?.id,
    person.email,
    selectedRole,
    inviteToOrganization,
  ])

  const handleManageInvites = useCallback(() => {
    window.open(`/orgs/${activeOrgId}/profile`, '_blank')
  }, [activeOrgId])

  const name = getPersonFirstName(person, null).trim() || 'Unknown'

  const hasEmail = person.email && person.email.length > 0

  // If the person has no email or is already a user, don't show the invite button
  if (!hasEmail || !!person.user_id) {
    return null
  }

  return (
    <>
      {hasBeenInvited ? (
        <IconButton
          tooltip={`Waiting for ${name} to accept their invite. Click here to manage invites.`}
          tooltipContentProps={{
            className: 'max-w-[372px]',
          }}
          onClick={handleManageInvites}
        >
          <LuClock
            size={16}
            className="text-primary-900 dark:text-primary-100"
          />
        </IconButton>
      ) : (
        <IconButton
          tooltip={
            maximumUsersReached
              ? "✋ You've reached your user limit! Contact support to add more team members."
              : !canInviteMembers
                ? 'You are not authorized to invite members. Please contact your organization administrator for assistance.'
                : org
                  ? `✨ Invite ${name} to join ${org.name} on Sweetspot.`
                  : `✨ Invite ${name} to join Sweetspot.`
          }
          tooltipContentProps={{
            className: 'max-w-[372px]',
          }}
          disabled={isInviting || maximumUsersReached || !canInviteMembers}
          onClick={handleOpen}
        >
          <LuUserPlus
            size={16}
            className="text-primary-900 dark:text-primary-100"
          />
        </IconButton>
      )}
      {open && (
        <AlertDialog open={open} onOpenChange={setOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Invite {name} to Sweetspot?</AlertDialogTitle>
            </AlertDialogHeader>
            <AlertDialogDescription className="space-y-4">
              <p>
                Inviting {name} to Sweetspot will allow them to access and use
                Sweetspot&apos;s features.{' '}
                {org ? (
                  <span>
                    They will be added to your organization,{' '}
                    <span className="font-semibold">{org.name}</span>.
                  </span>
                ) : (
                  ''
                )}
              </p>
              <p>
                We&apos;ll send an email to{' '}
                <span className="font-semibold">{person.email}</span> with a
                link to accept the invite.
              </p>
              <div className="flex flex-col gap-1.5">
                <Label>Role</Label>
                <Select
                  value={selectedRole}
                  onValueChange={setSelectedRole}
                  disabled={isInviting}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue>{selectedRole}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {availableRoles.map((role) => (
                      <SelectItem key={role} value={role}>
                        {role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-primary-500 dark:text-primary-400">
                  This role determines the user&apos;s permissions in Sweetspot.
                </p>
              </div>
            </AlertDialogDescription>
            <AlertDialogFooter className="mt-2">
              <Button variant="outline-compact" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant="positive-compact"
                onClick={handleInvite}
                disabled={isInviting}
              >
                Send Invite
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  )
}

export const ActionsCell: React.FC<CellContext<EntityPerson, unknown>> = ({
  row,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const permissionLoader = usePermissionLoader()

  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false)
  const [menuOpen, setMenuOpen] = useState<boolean>(false)
  const skipNextTagResetRef = useRef<boolean>(false)

  const person = row.original
  const {
    comboboxOpen,
    selectedTagIds,
    hasPendingTagChanges,
    isClearSelected,
    isSaving,
    handleOpenChange,
    handleToggleTag,
    handleCancel,
    handleClear,
    handleSave,
  } = useEntityTagCombobox({ entity: person })

  const handleOpenDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(true)
  }, [setDeleteDialogOpen])

  // Pre-warm permission cache on hover to avoid flicker
  const handleMouseEnter = useCallback(() => {
    if (permissionLoader && person.id) {
      permissionLoader.loadMany([
        {
          resourceType: 'entity',
          resourceId: person.id,
          permission: 'write',
        },
      ])
    }
  }, [permissionLoader, person.id])

  const handleMenuOpenChange = useCallback(
    (open: boolean) => {
      setMenuOpen(open)

      if (open && permissionLoader && person.id) {
        void permissionLoader.loadMany([
          {
            resourceType: 'entity',
            resourceId: person.id,
            permission: 'write',
          },
        ])
      }

      if (!open && skipNextTagResetRef.current) {
        skipNextTagResetRef.current = false
        return
      }

      if (!open) {
        handleCancel()
      }
    },
    [handleCancel, permissionLoader, person.id],
  )

  const handleCancelTagEdit = useCallback(() => {
    handleCancel()
    setMenuOpen(false)
  }, [handleCancel])

  const handleSaveTagEdit = useCallback(async () => {
    const saved = await handleSave()

    if (!saved) {
      return
    }

    skipNextTagResetRef.current = true
    setMenuOpen(false)
  }, [handleSave])

  return (
    <div
      className={twMerge('flex items-center justify-end gap-1 pr-3')}
      onMouseEnter={handleMouseEnter}
    >
      {deleteDialogOpen && (
        <DeletePeopleDialog
          personIds={[person.id]}
          open={deleteDialogOpen}
          setOpen={setDeleteDialogOpen}
        />
      )}
      <InviteToOrganizationDialog person={person} />
      <DropdownMenu open={menuOpen} onOpenChange={handleMenuOpenChange}>
        <DropdownMenuTrigger asChild={true}>
          <IconButton
            tooltip="More Actions"
            tooltipProps={{ delayDuration: 500 }}
          >
            <HiEllipsisHorizontal
              size={16}
              className="flex-shrink-0 text-primary-900 dark:text-primary-100"
            />
          </IconButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem>
            <Link
              href={`/orgs/${activeOrgId}/library/people/${person.id}`}
              className="flex items-center"
            >
              <HiOutlineEye
                size={16}
                className="flex-shrink-0 text-primary-700 dark:text-primary-300 mr-2"
              />
              <span>View Profile</span>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSub open={comboboxOpen} onOpenChange={handleOpenChange}>
            <ResourcePermissionGate
              resourceType="entity"
              resourceId={person.id}
              permission="write"
              fallback="disable"
              asChild
            >
              <DropdownMenuSubTrigger className="flex items-center gap-x-2">
                <HiOutlineTag
                  size={16}
                  className="text-primary-500 dark:text-primary-400"
                />
                <span>Edit Tags</span>
              </DropdownMenuSubTrigger>
            </ResourcePermissionGate>
            <DropdownMenuSubContent className="overflow-hidden p-0">
              <TagDropdownMenuEditSubContent
                selectedTagIds={selectedTagIds}
                onSelect={handleToggleTag}
                onCancel={handleCancelTagEdit}
                onClear={handleClear}
                onSave={handleSaveTagEdit}
                clearActive={isClearSelected}
                isSaving={isSaving}
                saveDisabled={!hasPendingTagChanges}
              />
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuSeparator />
          <ResourcePermissionGate
            resourceType="entity"
            resourceId={person.id}
            permission="write"
            fallback="disable"
            asChild
          >
            <DropdownMenuItem
              onClick={handleOpenDeleteDialog}
              className="flex items-center gap-x-2 text-red-600 dark:text-red-500"
            >
              <HiOutlineTrash className="w-4 h-4" />
              <span>Delete</span>
            </DropdownMenuItem>
          </ResourcePermissionGate>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

export const PeopleSelectCell: React.FC<CellContext<EntityPerson, unknown>> = ({
  row,
  table,
  cell,
}) => {
  const lastSelectedRowId = usePeopleLastSelectedRowId()
  const setLastSelectedRowId = usePeopleSetLastSelectedRowId()

  return (
    <ShiftSelectCell
      row={row}
      table={table}
      cell={cell}
      lastSelectedRowId={lastSelectedRowId}
      setLastSelectedRowId={setLastSelectedRowId}
    />
  )
}
